import React, { useState } from 'react';
import { Tooltip, Box, Modal, Button, Typography } from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getModalBoxStyle } from '../../../../utils/commonFunc';

const EmailHistory = ({ children, invoiceId, onError, onUnsendSuccess, fetchInvoices }) => {
  const [open, setOpen] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, invoiceId: null });
  const [unsendLoading, setUnsendLoading] = useState(false);

  const handleTooltipOpen = () => {
    setOpen(true);
    // Always fetch email history on every hover
    fetchEmailHistory();
  };

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const fetchEmailHistory = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/admin/invoice/sent-email-history/${invoiceId}`);
      
      if (res?.status === 200) {
        setEmailHistory(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching email history:', err);
      setEmailHistory([]);
      if (onError) {
        onError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnsend = (emailId) => {
    setConfirmModal({ open: true, invoiceId: invoiceId });
  };

  const handleConfirmUnsend = async () => {
    try {
      setUnsendLoading(true);
      const response = await axios.post(`/admin/invoice/unsend-email/${confirmModal.invoiceId}`, {}, {
      });

      if (response.status === 200) {
        toast.success('Invoice marked as unsent successfully!');
        setConfirmModal({ open: false, invoiceId: null });
        setOpen(false); // Close tooltip
        fetchInvoices();
        
        if (onUnsendSuccess) {
          onUnsendSuccess(confirmModal.invoiceId);
        }
      }
    } catch (error) {
      toast.error('Failed to mark invoice as unsent. Please try again.');
    } finally {
      setUnsendLoading(false);
    }
  };

  const handleCancelUnsend = () => {
    setConfirmModal({ open: false, invoiceId: null });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const renderTooltipContent = () => {
    if (loading) {
      return (
        <div style={{ padding: '8px', color: '#fff' }}>
          Loading email history...
        </div>
      );
    }

    if (emailHistory.length === 0) {
      return (
        <div style={{ padding: '8px', color: '#fff' }}>
          No email history found
        </div>
      );
    }

    return (
      <div style={{ padding: '8px', maxWidth: '350px' }}>
        <div style={{ 
          fontWeight: 'bold', 
          marginBottom: '8px', 
          color: '#fff',
          fontSize: '14px'
        }}>
          Email History ({emailHistory.length})
        </div>
        {emailHistory.map((email, index) => (
          <div key={index} style={{ 
            marginBottom: index < emailHistory.length - 1 ? '8px' : '0', 
            padding: '8px', 
            backgroundColor: 'rgba(255,255,255,0.1)', 
            borderRadius: '4px',
            fontSize: '12px'
          }}>
            <div style={{ 
              color: '#fff', 
              fontWeight: '500',
              marginBottom: '3px'
            }}>
              To: {email.toEmail}
            </div>
            <div style={{ 
              color: '#e0e0e0', 
              marginBottom: '3px'
            }}>
              Sent: {formatDate(email.sentDate)}
            </div>
            <div style={{ 
              color: '#e0e0e0'
            }}>
              By: {email.createdByName}
            </div>
          </div>
        ))}

       <div>
         <button 
           onClick={(e) => {
             e.stopPropagation();
             handleUnsend();
           }} 
           style={{
             marginTop: '8px', 
             cursor: 'pointer', 
             padding: '4px 8px', 
             borderRadius: '4px', 
             marginBottom: '8px',
             backgroundColor: '#ff4444',
             color: 'white',
             fontWeight: '500'
           }}
         >
           Mark as unsent
         </button>
       </div>
      </div>
    );
  };

  return (
    <>
    <Tooltip
      title={renderTooltipContent()}
      open={open}
      onClose={handleTooltipClose}
      onOpen={handleTooltipOpen}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            fontSize: '12px',
            maxHeight: '300px',
            maxWidth: '400px',
            overflowY: 'auto',
            '& .MuiTooltip-arrow': {
              color: 'rgba(0, 0, 0, 0.9)',
            },
          },
        },
      }}
    >
      {children}
    </Tooltip>

     <Modal
        open={confirmModal.open}
        onClose={handleCancelUnsend}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={getModalBoxStyle()}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Are you sure you want to mark this invoice as unsent?
          </Typography>
          <div
            style={{ margin: '25px 0 0', float: 'right', gap: '10px', display: 'flex', alignItems: 'center' }}
          >
            <Button
              onClick={handleCancelUnsend}
              sx={{ float: 'right', backgroundColor: '#62b5e514' }}
              autoFocus
            >
              No
            </Button>
            <Button
              onClick={handleConfirmUnsend}
              sx={{ float: 'right', backgroundColor: '#62b5e514' }}
              autoFocus
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Yes'}
            </Button>
          </div>
        </Box>
      </Modal>
    </>
  );
};

export default EmailHistory;
